import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import styled from "styled-components/macro";
import { theme } from "./theme";

const ToastContext = createContext(null);

const VARIANT_COLOR = {
  success: theme.success,
  error: theme.danger,
  info: theme.primary,
};

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message, variant = "info", duration = 4000) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, message, variant }]);
      setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <Stack>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} variantColor={VARIANT_COLOR[toast.variant] || theme.primary}>
            {toast.message}
          </ToastItem>
        ))}
      </Stack>
    </ToastContext.Provider>
  );
};

const useToast = () => {
  const showToast = useContext(ToastContext);
  return showToast || (() => {});
};

const Stack = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 100;
`;

const ToastItem = styled.div`
  min-width: 240px;
  max-width: 340px;
  padding: 12px 16px;
  background: ${theme.surface};
  border-left: 3px solid ${({ variantColor }) => variantColor};
  border-radius: ${theme.radiusSm};
  box-shadow: ${theme.shadowHover};
  font-family: ${theme.fontFamily};
  font-size: 13px;
  color: ${theme.text};
  animation: toastSlideIn 200ms ease;

  @keyframes toastSlideIn {
    from {
      opacity: 0;
      transform: translateX(16px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

export { ToastProvider, useToast };
